import { computed, onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import type {
  IUpdateReservationGuestDto,
  TReservationGuestSort,
} from '@/api/services/reservations.service';
import type {
  IReservationGuest,
  IReservationGuestDetail,
} from '@/interfaces/IReservationGuest';
import { useRestaurantStore } from '@/stores/restaurant.store';

/** Restaurant-wide guest search. Branch switches still invalidate in-flight views. */
export function useReservationGuests() {
  const restaurant = useRestaurantStore();
  const search = ref('');
  const query = ref('');
  const page = ref(1);
  const sortBy = ref<TReservationGuestSort>('lastVisitAt');
  const sortDesc = ref(true);
  const guests = ref<IReservationGuest[] | null>(null);
  const total = ref(0);
  const loading = ref(false);
  const error = ref(false);
  const totalPages = computed(() => Math.max(1, Math.ceil(total.value / 20)));
  let alive = true;
  let sequence = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const scope = computed(
    () => `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}`
  );

  async function load() {
    const request = ++sequence;
    const key = scope.value;
    const current = () => alive && request === sequence && key === scope.value;
    const id = restaurant.restaurant?.id;
    if (!id) {
      guests.value = null;
      total.value = 0;
      loading.value = false;
      return;
    }
    loading.value = true;
    error.value = false;
    try {
      const result = await ReservationsService.searchGuests(id, {
        search: query.value,
        page: page.value,
        sortBy: sortBy.value,
        sortDesc: sortDesc.value,
      });
      if (!current()) return;
      total.value = result.total;
      if (page.value > totalPages.value) {
        page.value = totalPages.value;
        return;
      }
      guests.value = result.items;
    } catch {
      if (current()) {
        error.value = true;
        guests.value = null;
        total.value = 0;
      }
    } finally {
      if (current()) loading.value = false;
    }
  }
  watch(search, (value) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (alive) {
        query.value = value.trim();
        page.value = 1;
      }
    }, 400);
  });
  watch(scope, () => {
    ++sequence;
    guests.value = null;
    total.value = 0;
    page.value = 1;
  });
  watch(
    [scope, query, page, sortBy, sortDesc],
    () => {
      void load();
    },
    { immediate: true }
  );
  function sort(key: TReservationGuestSort) {
    sortDesc.value = sortBy.value === key ? !sortDesc.value : key !== 'name';
    sortBy.value = key;
    page.value = 1;
  }
  onScopeDispose(() => {
    alive = false;
    ++sequence;
    clearTimeout(timer);
  });
  return {
    search,
    page,
    sortBy,
    sortDesc,
    guests,
    total,
    totalPages,
    loading,
    error,
    load,
    sort,
  };
}

/** Keeps editable fields stable across saves and rejects responses for a departed scope. */
export function useReservationGuest(customerId: Readonly<Ref<number>>) {
  const restaurant = useRestaurantStore();
  const guest = ref<IReservationGuestDetail | null>(null);
  const notes = ref('');
  const tags = ref<string[]>([]);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref(false);
  const saveError = ref(false);
  const dirty = computed(
    () =>
      !!guest.value &&
      (notes.value !== (guest.value.reservationNotes ?? '') ||
        JSON.stringify(tags.value) !== JSON.stringify(guest.value.tags ?? []))
  );
  const scope = computed(
    () =>
      `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}:${customerId.value}`
  );
  let alive = true;
  let sequence = 0;
  let saveSequence = 0;
  async function load() {
    if (saving.value) return;
    const request = ++sequence;
    const key = scope.value;
    const current = () => alive && request === sequence && key === scope.value;
    loading.value = true;
    error.value = false;
    try {
      const result = await ReservationsService.getGuest(customerId.value);
      if (!current()) return;
      if (result.restaurantId !== restaurant.restaurant?.id) {
        guest.value = null;
        error.value = true;
        return;
      }
      const same = guest.value?.customerId === result.customerId;
      const preserveNotes =
        same && notes.value !== (guest.value?.reservationNotes ?? '');
      const preserveTags =
        same &&
        JSON.stringify(tags.value) !== JSON.stringify(guest.value?.tags ?? []);
      guest.value = result;
      if (!preserveNotes) notes.value = result.reservationNotes ?? '';
      if (!preserveTags) tags.value = [...(result.tags ?? [])];
    } catch {
      if (current()) error.value = true;
    } finally {
      if (current()) loading.value = false;
    }
  }
  async function save(): Promise<boolean> {
    if (
      !guest.value ||
      saving.value ||
      !dirty.value ||
      notes.value.length > 10000
    )
      return false;
    const key = scope.value;
    const request = ++saveSequence;
    const current = () =>
      alive && key === scope.value && request === saveSequence;
    const sentNotes = notes.value;
    const sentTags = [...tags.value];
    const dto: IUpdateReservationGuestDto = {};
    if (sentNotes !== (guest.value.reservationNotes ?? ''))
      dto.reservationNotes = sentNotes.trim() || null;
    if (JSON.stringify(sentTags) !== JSON.stringify(guest.value.tags ?? []))
      dto.tags = sentTags.length ? sentTags : null;
    // A pending refresh must not replace a newer committed edit.
    ++sequence;
    loading.value = false;
    saving.value = true;
    saveError.value = false;
    try {
      const updated = await ReservationsService.updateGuest(
        customerId.value,
        dto
      );
      if (!current() || !guest.value) return false;
      guest.value = { ...guest.value, ...updated };
      if (notes.value === sentNotes)
        notes.value = updated.reservationNotes ?? '';
      if (JSON.stringify(tags.value) === JSON.stringify(sentTags))
        tags.value = [...(updated.tags ?? [])];
      return true;
    } catch {
      if (current()) saveError.value = true;
      return false;
    } finally {
      if (current()) saving.value = false;
    }
  }
  watch(
    scope,
    () => {
      ++saveSequence;
      saving.value = false;
      saveError.value = false;
      guest.value = null;
      notes.value = '';
      tags.value = [];
      void load();
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    alive = false;
    ++sequence;
    ++saveSequence;
  });
  return {
    guest,
    notes,
    tags,
    loading,
    saving,
    error,
    saveError,
    dirty,
    load,
    save,
  };
}
